// Pull the canonical formatted address out of a place's enrichment
// `sections` array (Google Places returns it under {title: 'Address',
// items: ['1 Rue du Mail, 75002 Paris, France']}). Returns '' if no
// such section is present.
export function addressFromSections(place: any): string {
  const sections = place?.sections;
  if (!Array.isArray(sections)) return '';
  const section = sections.find((s: any) => s?.title === 'Address');
  if (!section) return '';
  const item = Array.isArray(section.items) ? section.items[0] : null;
  if (typeof item === 'string') return item.trim();
  if (item && typeof item === 'object' && typeof item.value === 'string') return item.value.trim();
  return '';
}

// Defensively normalize a place's display address. Resolution order:
//  1. The Google-enriched `sections[Address]` if present — that's the
//     canonical formatted address straight from Google Places and is
//     always correct when the place was added via autocomplete.
//  2. The top-level `place.address` string — but only if it doesn't
//     look like an LLM-stringified structured object (we've seen the
//     agent occasionally save `{"number":"41","street":"rue"}` here
//     when it didn't have the full street). Parse and format if so.
//  3. The original string verbatim.
export function normalizePlaceAddress(place: any): string {
  const sectionAddr = addressFromSections(place);
  if (sectionAddr) return sectionAddr;

  const addr = place?.address;
  if (addr == null) return '';
  if (typeof addr === 'string') {
    const trimmed = addr.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === 'object') return formatAddressObject(parsed);
      } catch { /* not JSON — fall through and return the original string */ }
    }
    return trimmed;
  }
  if (typeof addr === 'object') return formatAddressObject(addr as Record<string, unknown>);
  return String(addr);
}

export function formatAddressObject(obj: Record<string, unknown>): string {
  const pick = (...keys: string[]): string => {
    for (const k of keys) {
      const v = obj[k];
      if (typeof v === 'string' && v.trim()) return v.trim();
      if (typeof v === 'number') return String(v);
    }
    return '';
  };
  // 1. If the upstream included a complete formatted string, use it
  //    verbatim — that's the most reliable representation.
  const formatted = pick(
    'formatted_address', 'formattedAddress', 'shortFormattedAddress',
    'displayAddress', 'fullAddress', 'full', 'text', 'value',
  );
  if (formatted) return formatted;

  // 2. Otherwise compose from the common component fields, including a
  //    catch-all for any extra string values so a partial structure
  //    (e.g. {number, street, neighborhood}) still surfaces everything
  //    rather than dropping the unrecognized parts.
  const knownKeys = new Set([
    'number', 'streetNumber', 'street_number', 'house_number',
    'street', 'streetName', 'street_name', 'route', 'road',
    'city', 'town', 'locality',
    'state', 'region', 'province', 'administrative_area_level_1',
    'zip', 'zipCode', 'postal_code', 'postalCode',
    'country',
  ]);
  const number = pick('number', 'streetNumber', 'street_number', 'house_number');
  const street = pick('street', 'streetName', 'street_name', 'route', 'road');
  const streetLine = [number, street].filter(Boolean).join(' ').trim();
  const city = pick('city', 'town', 'locality');
  const state = pick('state', 'region', 'province', 'administrative_area_level_1');
  const zip = pick('zip', 'zipCode', 'postal_code', 'postalCode');
  const country = pick('country');
  const stateZip = [state, zip].filter(Boolean).join(' ').trim();
  const extras = Object.entries(obj)
    .filter(([k, v]) => !knownKeys.has(k) && typeof v === 'string' && (v as string).trim())
    .map(([, v]) => (v as string).trim());
  const parts = [streetLine, ...extras, city, stateZip, country].filter(Boolean);
  if (parts.length > 0) return parts.join(', ');
  // Last resort: join whatever string values are present in field order.
  const values = Object.values(obj).filter(v => typeof v === 'string' && v.trim()) as string[];
  return values.join(', ');
}

// Extract a short "street · neighborhood" preview from a full address string.
// Drops long numeric tokens (zip/postal) and bare country/region codes, then
// keeps the first two remaining parts. Dedups the identical inline one-liner
// previously repeated at the search-results and main-list row sites.
export function formatShortAddress(address: string | undefined): string {
  const addressParts = address?.split(',').map((s: string) => s.trim()).filter((s: string) => s && !/^\d{4,}/.test(s) && !/^(USA?|US|United States|CA|France|UK|Italy|Spain|Germany|Japan)$/i.test(s)) || [];
  return addressParts.slice(0, 2).join(' · ');
}
